import React, { useMemo, useEffect, memo } from 'react'
import injectSheet from 'react-jss'
import styles from './slide.styles'
import Slide from '../../../components/slide/slide.jsx'
import Heading from '../../../components/heading/heading.jsx'
import Option from './option/option.jsx'
import Reset from './reset/reset.jsx'
import { useSerializedExercise, usePrevious } from './hooks'
import Gap from './gap/gap.jsx'
import { accessibilityColors, backgroundColors } from '../../../colors'
import { withPresentation } from '../../../presentation-connectors'

const ExerciseSlide = memo(
  ({ classes, accessibilityColor, slides, id, slidesState, setSlideState }) => {
    const data = slides[id]

    const exercise = useSerializedExercise(data.text)

    // Store state on presentation level to preserve it when switching between slides
    const givenAnswers = useMemo(() => slidesState[data.id] || [], [
      slidesState,
      data.id
    ])
    const setGivenAnswers = value => setSlideState(data.id, value)

    const previousData = usePrevious(data)

    // Reset answers when slide's being updated through the builder
    useEffect(
      () => {
        if (!previousData) return
        setGivenAnswers([])
      },
      [data] // eslint-disable-line
    )

    const activeQuestionIndex = useMemo(() => givenAnswers.length, [
      givenAnswers
    ])

    const { isGiven, answersMap } = useMemo(() => {
      const isGiven = {}
      const answersMap = {}
      givenAnswers.forEach((answer, index) => {
        isGiven[answer.index] = true
        answersMap[index] = answer.label
      })
      return {
        isGiven,
        answersMap
      }
    }, [givenAnswers])

    const colors = useMemo(
      () => ({
        accessibilityColor: accessibilityColors[accessibilityColor],
        bgColor: backgroundColors[data.secondaryColor].color,
        bgTextColor: backgroundColors[data.secondaryColor].contrasting
      }),
      [accessibilityColor, data]
    )

    const slideData = useMemo(
      () => ({
        title: data.title,
        options: data.options
      }),
      [data]
    )

    return (
      <Slide
        className={classes.container}
        style={{ backgroundColor: colors.bgColor }}>
        <Heading
          className={classes.title}
          style={{ color: colors.bgTextColor }}>
          {slideData.title}
        </Heading>
        <div
          className={classes.paper}
          style={{ backgroundColor: colors.accessibilityColor }}>
          {!!givenAnswers.length && (
            <Reset
              id={id}
              className={classes.reset}
              onClick={() => setGivenAnswers([])}
            />
          )}

          <div className={classes.exercise}>
            {exercise.map((item, index) => {
              switch (item.type) {
                case 'text':
                  return <span key={index}>{item.value}</span>
                case 'newLine':
                  return <br key={index} />
                case 'gap':
                  return (
                    <Gap
                      id={id}
                      active={activeQuestionIndex === item.index}
                      key={index}
                      answer={answersMap[item.index]}
                      color={colors.bgColor}
                    />
                  )
                default:
                  return null
              }
            })}
          </div>

          <div className={classes.options}>
            {slideData.options.map((option, index) => (
              <Option
                answered={isGiven[index]}
                onClick={() =>
                  setGivenAnswers([...givenAnswers, { index, label: option }])
                }
                key={index}
                colors={colors}>
                {option}
              </Option>
            ))}
          </div>
        </div>
      </Slide>
    )
  }
)

export default withPresentation(injectSheet(styles)(ExerciseSlide))
